/**
 * The bridge to NEAR, read from this chain's side (blackbeard/observer#39).
 *
 * Beside the NEAR Intents listing because it answers the other half of the
 * same question. The listing says how much of the token exists on NEAR and the
 * most there could be (#36); this says what is actually held here against it,
 * and who moved it across — by address, never by person.
 *
 * Two kinds of figure, and the panel keeps them apart: what custody holds and
 * what NEAR has minted are state, read now; deposits and withdrawals are summed
 * over this observer's index, whose range is printed beside them. The flows
 * table reports what moved and does not judge it: a depositor is an address the
 * bridge's fee payer funded, which says nothing about who controls it or why
 * they bridged.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import type { BridgeMovement } from '../api/generated/BridgeMovement'
import type { BridgeSummary } from '../api/generated/BridgeSummary'
import { RequestFailed, fetchBridge } from '../api/rest'
import { height as fmtHeight, shortAddress, tokens, utc, when } from '../lib/format'
import { href } from '../lib/routes'
import { Field } from './Field'

const REFRESH_MS = 60_000

/** How each movement reads in the table. */
const KIND: Record<string, string> = {
  deposit: 'deposited',
  withdrawal: 'withdrawn',
  other_inflow: 'sent to custody',
}

export function BridgePanel({
  chain,
  decimals,
  symbol,
}: {
  chain: string
  decimals: number
  symbol: string
}) {
  // `undefined` while loading, `null` when this chain has no bridge configured.
  const [data, setData] = useState<BridgeSummary | null | undefined>(undefined)
  const [older, setOlder] = useState<BridgeMovement[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let controller = new AbortController()
    const load = () => {
      controller = new AbortController()
      fetchBridge(chain, null, controller.signal)
        .then((d) => {
          setData(d)
          setError(null)
        })
        .catch((e: unknown) => {
          if (controller.signal.aborted) return
          if (e instanceof RequestFailed && e.status === 404) {
            setData(null)
            return
          }
          setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
        })
    }
    setOlder([])
    setCursor(null)
    load()
    const id = window.setInterval(load, REFRESH_MS)
    return () => {
      window.clearInterval(id)
      controller.abort()
    }
  }, [chain])

  if (data === null) return null

  const d = data?.decimals ?? decimals
  const amount = (raw: string | null | undefined) => (raw ? `${tokens(raw, d)} ${symbol}` : '—')
  const signed = (raw: string | null | undefined) => {
    if (!raw) return '—'
    const negative = raw.startsWith('-')
    return `${negative ? '−' : '+'}${tokens(negative ? raw.slice(1) : raw, d)} ${symbol}`
  }
  const next = older.length > 0 ? cursor : (data?.next ?? null)
  const loadOlder = () => {
    if (!next) return
    fetchBridge(chain, next)
      .then((page) => {
        setOlder((rows) => [...rows, ...page.movements])
        setCursor(page.next)
      })
      .catch(() => {})
  }
  const rows = data ? [...data.movements, ...older] : []
  const short = data?.locked_less_minted?.startsWith('-') === true
  // A shortfall is the alarm only when both figures describe the same moment.
  // Between a custody movement and the next NEAR supply read the gap can sit
  // either side of zero with nothing wrong: a withdrawal burns on NEAR first
  // and releases here after, and the supply is read every few minutes.
  const alarm = short && data?.gap_settled === true
  const range =
    data && data.indexed_from !== null && data.indexed_to !== null
      ? `blocks ${fmtHeight(data.indexed_from)}–${fmtHeight(data.indexed_to)}`
      : 'nothing indexed yet'

  return (
    <section className="panel" style={{ marginBottom: 26 }}>
      <div className="panel-head">
        <h2 className="panel-title">Bridge</h2>
        <span className="eyebrow">custody on this chain against {symbol} minted on NEAR</span>
      </div>

      {error && <p className="empty">{error}</p>}
      {!error && data === undefined && <p className="empty">Reading the bridge…</p>}

      {data && alarm && (
        <p className="panel-note" role="alert">
          <strong>
            More {symbol} is minted on NEAR than custody holds against it, by{' '}
            {signed(data.locked_less_minted).slice(1)}.
          </strong>{' '}
          The NEAR supply was read after the last movement through custody, so the two figures
          describe the same moment: either some {symbol} on NEAR is unbacked, or one of the two was
          misread.
        </p>
      )}
      {data && short && !alarm && (
        <p className="panel-note">
          Custody moved after the NEAR supply was last read, so the two figures are minutes apart
          and the gap below is not yet meaningful. It settles at the next supply read.
        </p>
      )}

      {data && (
        <>
          <div className="node-grid">
            <Field
              label="Held in custody"
              value={amount(data.custody_balance)}
              note="its balance now"
              title={data.custody}
            />
            <Field
              label="Locked against NEAR"
              value={amount(data.locked)}
              note={`custody less ${amount(data.other_inflows)} that was never a deposit`}
            />
            <Field
              label="Minted on NEAR"
              value={amount(data.minted)}
              note={
                data.minted_checked_at
                  ? `qtc.omft.near supply, read ${when(data.minted_checked_at)}`
                  : 'not read yet'
              }
            />
            <Field
              label="Locked less minted"
              value={signed(data.locked_less_minted)}
              note="positive: custody holds more than is minted"
            />
            <Field
              label="Deposits"
              value={`${fmtHeight(data.deposits)} from ${fmtHeight(data.depositors)} addresses`}
              note={`${amount(data.deposited)} in ${range}`}
            />
            <Field
              label="Withdrawals"
              value={fmtHeight(data.withdrawals)}
              note={`${amount(data.withdrawn)} in ${range}`}
            />
          </div>

          {rows.length === 0 ? (
            <p className="empty">Nothing has moved through custody in the blocks indexed.</p>
          ) : (
            <div className="scroll-x">
              <table className="board">
                <caption className="visually-hidden">
                  Movements through the bridge&apos;s custody account, newest first
                </caption>
                <thead>
                  <tr>
                    <th scope="col" className="left">
                      When
                    </th>
                    <th scope="col" className="left">
                      Movement
                    </th>
                    <th scope="col" className="left">
                      Address
                    </th>
                    <th scope="col">{symbol}</th>
                    <th scope="col" className="left">
                      Block
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((m) => (
                    <tr key={`${m.height}-${m.index}`}>
                      <td className="left numeral" title={utc(m.at)}>
                        {when(m.at)}
                      </td>
                      <td
                        className="left"
                        title={
                          m.funded_at !== null
                            ? `the fee payer funded this address at block ${fmtHeight(m.funded_at)}`
                            : undefined
                        }
                      >
                        {KIND[m.kind] ?? m.kind}
                      </td>
                      <td className="left">
                        <Link to={href({ chain, account: m.counterparty })} title={m.counterparty}>
                          {shortAddress(m.counterparty)}
                        </Link>
                      </td>
                      <td className="numeral">{tokens(m.amount, d)}</td>
                      <td className="left numeral">
                        <Link to={href({ chain, block: String(m.height) })}>
                          {fmtHeight(m.height)}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {next && (
            <div className="pager">
              <button className="segmented panel-button" onClick={loadOlder}>
                Older →
              </button>
            </div>
          )}

          <p className="panel-note">
            A <strong>deposit</strong> is a transfer into custody from an address the bridge&apos;s
            fee payer funded just before, so the depositor pays no fee; a{' '}
            <strong>withdrawal</strong> is a transfer out, released against {symbol} burned on NEAR.
            Anything else sent to custody — {amount(data.other_inflows)} so far, the first of it the
            seed custody started with — is not minted against, which is why it is left out of what
            is locked. Both accounts are named in this observer&apos;s configuration with the
            evidence for each; neither is inferred.
          </p>
          <p className="panel-note">
            What this cannot say: an address is not a person. One holder can use several deposit
            addresses and one address can deposit many times, and a depositor may be a miner, an
            exchange or anyone else — bridging says nothing about why. Custody and NEAR&apos;s
            supply are read now; deposits and withdrawals are summed over the blocks this observer
            has indexed ({range}), and are only the whole history if that range covers it.
          </p>
        </>
      )}
    </section>
  )
}
